import { componentInterface } from '@ardoq/component-interface';
import type {
  BubbleChartDataPointModel,
  BubbleChartTrackingCallbacks,
} from '@ardoq/bubble-chart';
import {
  startViewLoadTracking,
  recordViewModelReady,
  recordRenderComplete,
  DEFAULT_EXPECTED_DURATION,
} from 'tracking/durationTracking/viewPerformance';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { ViewIds } from '@ardoq/api-types';

type BubbleChartViewLoadMetrics = {
  componentCount: number;
  referenceCount: number;
  workspaceCount: number;
};

const calculateViewLoadMetrics = (
  dataPoints: BubbleChartDataPointModel[] | null | undefined
): BubbleChartViewLoadMetrics => {
  const normalizedDataPoints = dataPoints ?? [];

  const componentIdSet = new Set<string>(
    // BubbleChartDataPointModel.cid is the component identifier
    normalizedDataPoints.map(dataPoint => dataPoint.cid)
  );

  const workspaceIdSet = new Set<string>();
  componentIdSet.forEach(componentId => {
    const workspaceId = componentInterface.getWorkspaceId(componentId);
    if (workspaceId) {
      workspaceIdSet.add(workspaceId);
    }
  });

  return {
    componentCount: componentIdSet.size,
    // Bubble chart does not expose references directly
    referenceCount: 0,
    workspaceCount: workspaceIdSet.size,
  };
};

export const getBubbleChartTrackingCallbacks = (
  trackingId: string,
  dataPoints: BubbleChartDataPointModel[] | null | undefined
): BubbleChartTrackingCallbacks => {
  const viewLoadMetrics = calculateViewLoadMetrics(dataPoints);

  return {
    onViewModelReady: () => recordViewModelReady(trackingId, viewLoadMetrics),
    onRendered: () => recordRenderComplete(trackingId),
  };
};

export const startBubbleChartTracking = () =>
  startViewLoadTracking(
    viewIdToUserFriendlyName[ViewIds.BUBBLE],
    DEFAULT_EXPECTED_DURATION
  );
